import { useState, useRef, useEffect, useMemo } from "react"
import { MessageCircle, Send, Bot, Sparkles, Plus, ChevronDown, Zap } from "lucide-react"
import { Button } from "@/components/ui/button"
import TextareaAutosize from 'react-textarea-autosize'
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAuth } from "@/components/auth-context"
import { usePlan } from "@/hooks/use-plan"
import { chatService } from "@/lib/chat-service"
import type { ChatMessage } from "@/types/chat"
import { cn } from "@/lib/utils"
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useLocation, Link } from "react-router-dom"
import { useTranslation, Trans } from "react-i18next"

// Helper function para garantizar UUID sin depender solo de crypto.randomUUID (evita crashes móviles HTTP)
function generateSafeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch {
      // Ignorar e ir al fallback
    }
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16);
  });
}

// ─── Keywords para detectar solicitud de agente humano ────────────────────────
const HUMAN_KEYWORDS = [
    "hablar con un humano", "agente humano", "soporte humano",
    "hablar con una persona", "ponme con un agente", "contactar con soporte",
    "agente de verdad", "persona real", "no quiero hablar con una maquina",
    "hablar con alguien", "ayuda humana", "soporte tecnico real",
    "quiero hablar con un humano",
    "talk to a human", "human agent", "human support",
    "talk to a person", "put me with an agent", "contact support",
    "real agent", "real person", "I don't want to talk to a machine",
    "speak with someone", "human help", "real tech support",
    "I want to talk to a human"
]

export function SupportWidget() {
    const { t, i18n } = useTranslation()
    const { user, organization } = useAuth()
    const { isFreePlan } = usePlan()
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [inputValue, setInputValue] = useState("")
    const [messages, setMessages] = useState<ChatMessage[]>([])

    const scrollRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    // ─── Conversation ID (persistido en localStorage) ─────────────────────────
    const [conversationId, setConversationId] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("tempora_active_conversation_id") || generateSafeId()
    }
    return generateSafeId()
  })

    useEffect(() => {
        if (conversationId) {
            localStorage.setItem("tempora_active_conversation_id", conversationId)
        }
    }, [conversationId])

    const startNewConversation = () => {
        const newId = generateSafeId()
        setConversationId(newId)
        setInputValue("")
        setMessages([{
            id: "greeting",
            text: t('help.supportWidget.newConversationStarted'),
            sender: 'bot',
            timestamp: new Date(),
        }])
    }

    // ─── Cargar historial real desde Supabase ─────────────────────────────────
    useEffect(() => {
        const loadHistory = async () => {
            if (!user || !isOpen) return

            const history = await chatService.getChatHistory(user.id, conversationId)
            if (history.length > 0) {
                setMessages(history)
            } else {
                setMessages([{
                    id: "greeting",
                    text: t('help.supportWidget.greeting'),
                    sender: 'bot',
                    timestamp: new Date(),
                }])
            }
        }
        loadHistory()
    }, [isOpen, user, conversationId, t])

    // ─── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages, isTyping, isOpen])

    // ─── Focus al abrir ───────────────────────────────────────────────────────
    useEffect(() => {
        if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
    }, [isOpen])

    // ─── Mensajes sugeridos ───────────────────────────────────────────────────
    const suggestions = useMemo(() => [
        { label: t('help.supportWidget.suggestions.newProject'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.trackTime'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.exportReports'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.inviteTeam'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.modifyProfile'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.billingIssues'), type: 'standard' },
        { label: t('help.supportWidget.suggestions.talkToHuman'), type: 'human' },
    ], [t])

    // ─── Enviar mensaje (texto puro, sin imágenes — bot de soporte básico) ────
    const handleSend = async (e?: React.FormEvent, overrideText?: string, isHumanRequest = false) => {
        e?.preventDefault()

        const finalText = (overrideText || inputValue).trim()
        if (!finalText || isTyping) return

        const finalIsHumanRequest = isHumanRequest ||
            HUMAN_KEYWORDS.some(kw => finalText.toLowerCase().includes(kw))

        const userMsg: ChatMessage = {
            id: generateSafeId(),
            text: finalText,
            sender: 'user',
            timestamp: new Date(),
        }

        setMessages(prev => [...prev, userMsg])
        setInputValue("")
        setIsTyping(true)

        if (!user) return

        // Usa el endpoint ligero: solo RAG documental, sin tools de Supabase
        const response = await chatService.sendSupportMessage(
            userMsg.text,
            user.id,
            conversationId,
            organization?.id,
            undefined,           // Sin imagen
            messages,
            finalIsHumanRequest,
            i18n.language // Pasar el idioma actual
        )

        setMessages(prev => [...prev, response])
        setIsTyping(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    // Solo visible en la sección de ayuda
    if (!location.pathname.startsWith('/help')) return null

    return (
        <div className="fixed bottom-28 lg:bottom-8 right-4 lg:right-6 z-[60] flex flex-col items-end gap-3 lg:gap-4 font-sans">
            {/* ─── Chat Window ─────────────────────────────────────────────── */}
            <div className={cn(
                "fixed bottom-28 right-4 lg:absolute lg:bottom-0 lg:right-0",
                "w-[calc(100vw-2rem)] sm:w-[350px] h-[520px] max-h-[calc(100dvh-9rem)] lg:max-h-[calc(100dvh-6rem)]",
                "bg-background/95 backdrop-blur-xl border border-border shadow-2xl rounded-2xl flex flex-col overflow-hidden",
                "transition-all duration-300 origin-bottom-right z-[61]",
                isOpen
                    ? "opacity-100 scale-100 pointer-events-auto translate-y-0"
                    : "opacity-0 scale-95 pointer-events-none translate-y-4"
            )}>
                {/* Header */}
                <div className="p-4 bg-primary flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                            <Bot className="w-5 h-5 text-black" />
                        </div>
                        <div className="flex flex-col">
                            <h3 className="font-bold text-black text-sm">{t('help.supportWidget.title')}</h3>
                            <div className="flex items-center gap-1.5">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                                </span>
                                <span className="text-[10px] uppercase font-bold text-black/60 tracking-wider">{t('help.supportWidget.statusOnline')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-black hover:bg-white/20 rounded-lg px-2 h-8 flex items-center gap-1.5 transition-colors"
                            onClick={startNewConversation}
                            title={t('help.supportWidget.newConversation')}
                        >
                            <Plus className="w-4 h-4" />
                            <span className="text-[10px] font-bold uppercase tracking-tight">{t('help.supportWidget.restart')}</span>
                        </Button>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="text-black hover:bg-white/20 rounded-full h-8 w-8"
                            onClick={() => setIsOpen(false)}
                            title={t('help.supportWidget.collapse')}
                        >
                            <ChevronDown className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4 bg-white/50 dark:bg-zinc-950/50">
                    <div className="space-y-4 flex flex-col">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={cn(
                                    "flex flex-col max-w-[85%]",
                                    msg.sender === 'user' ? "self-end items-end" : "self-start items-start"
                                )}
                            >
                                {msg.id === 'greeting' && (
                                    <div className="flex items-center gap-2 mb-1 pl-1">
                                        <div className="bg-primary/10 p-1.5 rounded-full">
                                            <Bot className="w-4 h-4 text-primary" />
                                        </div>
                                        <span className="text-xs font-medium text-zinc-500">{t('help.supportWidget.assistantName')}</span>
                                    </div>
                                )}
                                <div className={cn(
                                    "rounded-2xl px-4 py-2.5 shadow-sm text-sm leading-relaxed border",
                                    msg.sender === 'user'
                                        ? "bg-primary text-primary-foreground rounded-br-sm border-primary"
                                        : "bg-background text-foreground rounded-bl-sm border-border"
                                )}>
                                    {msg.text && (
                                        <div className="prose dark:prose-invert prose-sm max-w-none break-words">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-blue-500" />,
                                                    p: ({ node: _node, ...props }) => <p {...props} className="m-0" />,
                                                }}
                                            >
                                                {msg.text}
                                            </ReactMarkdown>
                                        </div>
                                    )}
                                </div>
                                <span className="text-[10px] text-muted-foreground opacity-70 block mt-1 px-1">
                                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        ))}

                        {/* Quick suggestions */}
                        {messages.length <= 1 && (
                            <div className="flex flex-col gap-2 items-end mb-4 px-2 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSend(undefined, suggestion.label, suggestion.type === 'human')}
                                        className="px-4 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 text-xs rounded-full hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-all shadow-sm active:scale-95"
                                    >
                                        {suggestion.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isTyping && (
                            <div className="bg-white dark:bg-zinc-800 self-start p-4 rounded-2xl rounded-tl-sm shadow-sm border border-zinc-100 dark:border-zinc-700 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" />
                            </div>
                        )}
                        <div ref={scrollRef} />
                    </div>
                </ScrollArea>

                {/* Input Area */}
                <div className="p-3 bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-zinc-800 shrink-0 flex flex-col gap-2">
                    <div className="relative flex items-end gap-2 bg-muted/50 p-2 rounded-xl border border-input focus-within:ring-1 focus-within:ring-primary focus-within:border-primary transition-all shadow-sm">
                        <TextareaAutosize
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('help.supportWidget.typingPlaceholder')}
                            minRows={1}
                            maxRows={4}
                            className="flex-1 bg-transparent border-none resize-none px-2 py-1.5 text-sm focus:ring-0 focus:outline-none placeholder:text-muted-foreground no-scrollbar leading-relaxed"
                        />
                        <Button
                            onClick={(e) => handleSend(e)}
                            size="icon"
                            disabled={!inputValue.trim() || isTyping}
                            className={cn(
                                "h-9 w-9 rounded-xl shrink-0 transition-all shadow-sm",
                                inputValue.trim()
                                    ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 active:scale-95"
                                    : "bg-zinc-200 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-600"
                            )}
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        {/* Banner de upgrade para usuarios Free */}
                        {isFreePlan && (
                            <Link
                                to="/billing"
                                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 border border-primary/15 hover:bg-primary/10 transition-colors text-xs text-primary font-medium group"
                            >
                                <Zap className="w-3.5 h-3.5 shrink-0" />
                                <span className="flex-1">
                                    <Trans 
                                        i18nKey="help.supportWidget.upgradeBanner"
                                        components={{ strong: <strong /> }}
                                    />
                                </span>
                                <span className="text-[10px] opacity-70 group-hover:opacity-100">→</span>
                            </Link>
                        )}
                        <div className="flex justify-center">
                            <span className="text-[10px] text-zinc-400 flex items-center gap-1 select-none">
                                <Sparkles className="w-3 h-3 text-primary" /> Powered by n8n AI
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Floating Bubble ─────────────────────────────────────────── */}
            <Button
                onClick={() => setIsOpen(true)}
                size="lg"
                className={cn(
                    "rounded-full h-14 w-14 shadow-xl transition-all duration-300 hover:scale-105 bg-primary text-primary-foreground hover:bg-primary/90 lg:absolute lg:bottom-0 lg:right-0 z-[60] origin-center",
                    isOpen ? "opacity-0 scale-50 pointer-events-none" : "opacity-100 scale-100 pointer-events-auto",
                    "absolute bottom-0 right-0"
                )}
            >
                <MessageCircle className="h-7 w-7" />
            </Button>
        </div>
    )
}
